import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getImageUrl } from '../utils/imageHelper';
import authService from '../services/authService';
import Navbar from './Navbar';
import Footer from './Footer';
import BoardGameCoverPlaceholder from './BoardGameCoverPlaceholder';

const BoardGameDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [boardGame, setBoardGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDiscussionForm, setShowDiscussionForm] = useState(false);
  const [discussionTitle, setDiscussionTitle] = useState('');
  const [discussionContent, setDiscussionContent] = useState('');

  useEffect(() => {
    fetchBoardGame();
  }, [slug]);

  const fetchBoardGame = async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/board-games/slug/${slug}`);
      const data = await response.json();

      if (data.success) {
        setBoardGame(data.data);
      } else {
        setError(data.message || 'Board game not found');
      }
    } catch (err) {
      console.error('Error fetching board game:', err);
      setError('Failed to load board game');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDiscussion = async (e) => {
    e.preventDefault();
    
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      const response = await authService.post('/api/discussions', {
        title: discussionTitle,
        content: discussionContent,
        isGeneral: false,
        boardGameIds: [boardGame.id]
      });

      if (response.data.success) {
        navigate(`/discussions/${response.data.data.slug}`);
      }
    } catch (err) {
      console.error('Error creating discussion:', err);
      alert('Failed to create discussion');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-base-200">
        <Navbar />
        <div className="flex justify-center items-center min-h-[60vh]">
          <span className="loading loading-spinner loading-lg"></span>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !boardGame) {
    return (
      <div className="min-h-screen bg-base-200">
        <Navbar />
        <div className="container mx-auto px-4 py-8">
          <div className="alert alert-error">
            <span>{error || 'Board game not found'}</span>
          </div>
          <Link to="/board-games" className="btn btn-primary mt-4">
            Browse Board Games
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-200">
      <Navbar />
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header Section */}
          <div className="bg-base-100 rounded-lg shadow-lg overflow-hidden mb-8">
            <div className="grid md:grid-cols-3 gap-6 p-6">
              {/* Cover Image */}
              <div className="md:col-span-1">
                {boardGame.coverImage ? (
                  <img
                    src={getImageUrl(boardGame.coverImage)}
                    alt={boardGame.name}
                    className="w-full h-auto rounded-lg shadow-md"
                  />
                ) : (
                  <div className="w-full aspect-square bg-base-300 rounded-lg">
                    <BoardGameCoverPlaceholder className="h-full" />
                  </div>
                )}
              </div>

              {/* Game Info */}
              <div className="md:col-span-2">
                <h1 className="text-4xl font-bold mb-4">{boardGame.name}</h1>
                
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <p className="text-sm text-base-content/70">Players</p>
                    <p className="text-lg font-semibold">
                      {boardGame.minPlayers} - {boardGame.maxPlayers}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-base-content/70">Duration</p>
                    <p className="text-lg font-semibold">
                      {boardGame.duration ? `${boardGame.duration} min` : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-base-content/70">Complexity</p>
                    <p className="text-lg font-semibold capitalize">
                      {boardGame.complexity || 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-base-content/70">Year</p>
                    <p className="text-lg font-semibold">
                      {boardGame.yearPublished || 'N/A'}
                    </p>
                  </div>
                </div>

                {boardGame.designer && (
                  <p className="mb-2">
                    <span className="font-semibold">Designer:</span> {boardGame.designer}
                  </p>
                )}
                {boardGame.publisher && (
                  <p className="mb-2">
                    <span className="font-semibold">Publisher:</span> {boardGame.publisher}
                  </p>
                )}

                <div className="mt-6">
                  <p className="text-sm text-base-content/70">Available at {boardGame._count?.clubBoardGames || 0} clubs</p>
                  <p className="text-sm text-base-content/70">{boardGame._count?.discussionLinks || 0} discussions</p>
                </div>
              </div>
            </div>
          </div>

          {/* Description */}
          {boardGame.description && (
            <div className="bg-base-100 rounded-lg shadow-lg p-6 mb-8">
              <h2 className="text-2xl font-bold mb-4">About</h2>
              <p className="text-base-content/80 whitespace-pre-wrap">{boardGame.description}</p>
            </div>
          )}

          {/* Rules */}
          {boardGame.rules && (
            <div className="bg-base-100 rounded-lg shadow-lg p-6 mb-8">
              <h2 className="text-2xl font-bold mb-4">Rules</h2>
              <p className="text-base-content/80 whitespace-pre-wrap">{boardGame.rules}</p>
            </div>
          )}

          {/* Available at Clubs */}
          {boardGame.clubBoardGames && boardGame.clubBoardGames.length > 0 && (
            <div className="bg-base-100 rounded-lg shadow-lg p-6 mb-8">
              <h2 className="text-2xl font-bold mb-4">Available at These Clubs</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {boardGame.clubBoardGames.map((clubGame) => (
                  <Link
                    key={clubGame.club.id}
                    to={`/clubs/${clubGame.club.slug || clubGame.club.id}`}
                    className="card bg-base-200 hover:shadow-xl transition-shadow"
                  >
                    <div className="card-body">
                      {clubGame.club.image && (
                        <img
                          src={getImageUrl(clubGame.club.image)}
                          alt={clubGame.club.name}
                          className="w-full h-32 object-cover rounded-lg mb-2"
                        />
                      )}
                      <h3 className="card-title text-lg">{clubGame.club.name}</h3>
                      {clubGame.club.location && (
                        <p className="text-sm text-base-content/70">📍 {clubGame.club.location}</p>
                      )}
                      {clubGame.quantity > 1 && (
                        <p className="text-sm">Quantity: {clubGame.quantity}</p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Discussions */}
          <div className="bg-base-100 rounded-lg shadow-lg p-6 mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Discussions</h2>
              <button
                onClick={() => setShowDiscussionForm(!showDiscussionForm)}
                className="btn btn-primary btn-sm"
              >
                Start Discussion
              </button>
            </div>

            {showDiscussionForm && (
              <form onSubmit={handleCreateDiscussion} className="mb-6 p-4 bg-base-200 rounded-lg">
                <div className="form-control mb-4">
                  <label className="label">
                    <span className="label-text">Title</span>
                  </label>
                  <input
                    type="text"
                    value={discussionTitle}
                    onChange={(e) => setDiscussionTitle(e.target.value)}
                    className="input input-bordered"
                    required
                  />
                </div>
                <div className="form-control mb-4">
                  <label className="label">
                    <span className="label-text">Content</span>
                  </label>
                  <textarea
                    value={discussionContent}
                    onChange={(e) => setDiscussionContent(e.target.value)}
                    className="textarea textarea-bordered h-32"
                    required
                  ></textarea>
                </div>
                <div className="flex gap-2">
                  <button type="submit" className="btn btn-primary">
                    Create Discussion
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDiscussionForm(false)}
                    className="btn btn-ghost"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            )}

            {boardGame.discussionLinks && boardGame.discussionLinks.length > 0 ? (
              <div className="space-y-4">
                {boardGame.discussionLinks.map((link) => (
                  <Link
                    key={link.discussion.id}
                    to={`/discussions/${link.discussion.slug}`}
                    className="block p-4 bg-base-200 rounded-lg hover:shadow-md transition-shadow"
                  >
                    <h3 className="text-lg font-semibold mb-2">{link.discussion.title}</h3>
                    <div className="flex gap-4 text-sm text-base-content/70">
                      <span>👁️ {link.discussion.viewCount} views</span>
                      <span>💬 {link.discussion.replyCount} replies</span>
                      <span>
                        by {link.discussion.author.firstName} {link.discussion.author.lastName}
                      </span>
                    </div>
                  </Link>
                ))}
                {boardGame._count?.discussionLinks > boardGame.discussionLinks.length && (
                  <Link
                    to={`/discussions?boardGameId=${boardGame.id}`}
                    className="btn btn-outline btn-sm w-full"
                  >
                    View All Discussions
                  </Link>
                )}
              </div>
            ) : (
              <p className="text-base-content/70">
                No discussions yet. Be the first to start one!
              </p>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default BoardGameDetail;
